/**
 * Shared bulk-selection state for paginated list pages.
 * Parents own filters, APIs, and delete; this module only tracks selection mode/keys.
 */

export type BulkSelectionMode = 'page' | 'all_filtered';

export type BulkSelectionKey = number | string;

export interface BulkSelectionState<T extends BulkSelectionKey = BulkSelectionKey> {
    mode: BulkSelectionMode;
    /** Explicit keys when mode === 'page'. */
    selectedKeys: Set<T>;
    /** Keys unchecked after select-all-matching when mode === 'all_filtered'. */
    excludedKeys: Set<T>;
    /** Total matching records from the current list query (all_filtered). */
    matchingTotal: number;
}

export type BulkDeleteIdField = 'vehicle_ids' | 'ids' | 'vehicle_numbers' | 'selected_ids';

export type BulkDeleteExclusionField = 'excluded_ids' | 'excluded_vehicle_numbers';

export function createEmptySelection<T extends BulkSelectionKey = BulkSelectionKey>(): BulkSelectionState<T> {
    return {
        mode: 'page',
        selectedKeys: new Set<T>(),
        excludedKeys: new Set<T>(),
        matchingTotal: 0,
    };
}

export function clearSelection<T extends BulkSelectionKey>(
    _state?: BulkSelectionState<T>,
): BulkSelectionState<T> {
    return createEmptySelection<T>();
}

export function selectedCount<T extends BulkSelectionKey>(state: BulkSelectionState<T>): number {
    if (state.mode === 'all_filtered') {
        return Math.max(0, state.matchingTotal - state.excludedKeys.size);
    }
    return state.selectedKeys.size;
}

export function hasSelection<T extends BulkSelectionKey>(state: BulkSelectionState<T>): boolean {
    return selectedCount(state) > 0;
}

export function isKeySelected<T extends BulkSelectionKey>(
    state: BulkSelectionState<T>,
    key: T,
): boolean {
    if (state.mode === 'all_filtered') {
        return !state.excludedKeys.has(key);
    }
    return state.selectedKeys.has(key);
}

export function isPageFullySelected<T extends BulkSelectionKey>(
    state: BulkSelectionState<T>,
    pageKeys: readonly T[],
): boolean {
    return pageKeys.length > 0 && pageKeys.every((key) => isKeySelected(state, key));
}

/**
 * Toggle a single row. In all_filtered mode, uncheck adds to exclusions;
 * check removes from exclusions.
 */
export function toggleKey<T extends BulkSelectionKey>(
    state: BulkSelectionState<T>,
    key: T,
    checked: boolean,
): BulkSelectionState<T> {
    if (state.mode === 'all_filtered') {
        const excludedKeys = new Set(state.excludedKeys);
        if (checked) {
            excludedKeys.delete(key);
        } else {
            excludedKeys.add(key);
        }
        return {...state, excludedKeys};
    }

    const selectedKeys = new Set(state.selectedKeys);
    if (checked) {
        selectedKeys.add(key);
    } else {
        selectedKeys.delete(key);
    }
    return {...state, selectedKeys};
}

/**
 * Header checkbox: add/remove visible page keys only.
 * Selecting while in all_filtered switches to page mode with those keys.
 * Deselecting while in all_filtered excludes the visible page keys.
 */
export function selectVisiblePage<T extends BulkSelectionKey>(
    state: BulkSelectionState<T>,
    pageKeys: readonly T[],
    selected: boolean,
): BulkSelectionState<T> {
    if (state.mode === 'all_filtered') {
        if (selected) {
            return {
                mode: 'page',
                selectedKeys: new Set(pageKeys),
                excludedKeys: new Set<T>(),
                matchingTotal: 0,
            };
        }
        const excludedKeys = new Set(state.excludedKeys);
        for (const key of pageKeys) {
            excludedKeys.add(key);
        }
        return {...state, excludedKeys};
    }

    const selectedKeys = new Set(state.selectedKeys);
    if (selected) {
        for (const key of pageKeys) {
            selectedKeys.add(key);
        }
    } else {
        for (const key of pageKeys) {
            selectedKeys.delete(key);
        }
    }
    return {
        mode: 'page',
        selectedKeys,
        excludedKeys: new Set<T>(),
        matchingTotal: 0,
    };
}

export function selectAllMatching<T extends BulkSelectionKey>(
    state: BulkSelectionState<T>,
    matchingTotal: number,
): BulkSelectionState<T> {
    return {
        mode: 'all_filtered',
        selectedKeys: new Set<T>(),
        excludedKeys: new Set<T>(),
        matchingTotal: Math.max(0, matchingTotal),
    };
}

export function excludeKey<T extends BulkSelectionKey>(
    state: BulkSelectionState<T>,
    key: T,
): BulkSelectionState<T> {
    if (state.mode !== 'all_filtered') {
        return toggleKey(state, key, false);
    }
    const excludedKeys = new Set(state.excludedKeys);
    excludedKeys.add(key);
    return {...state, excludedKeys};
}

/**
 * Build preview/confirm POST body for existing bulk-delete endpoints.
 * Page mode uses legacy id field names; all_filtered sends mode + filters + exclusions.
 */
export function buildDeletePayload<T extends BulkSelectionKey>(
    state: BulkSelectionState<T>,
    options: {
        idField: BulkDeleteIdField;
        filters?: Record<string, unknown>;
        exclusionField?: BulkDeleteExclusionField;
    },
): Record<string, unknown> {
    if (state.mode === 'all_filtered') {
        const exclusionField =
            options.exclusionField ??
            (options.idField === 'vehicle_numbers'
                ? 'excluded_vehicle_numbers'
                : 'excluded_ids');
        return {
            mode: 'all_filtered',
            filters: options.filters ?? {},
            [exclusionField]: Array.from(state.excludedKeys),
        };
    }

    return {
        mode: 'page',
        [options.idField]: Array.from(state.selectedKeys),
    };
}
